import { useSearchParams } from "react-router-dom";
import ProjectCard from "./ProjectCard";
import PillarModal from "./PillarModal";
import ProjectModal from "./ProjectModal";
import PillarGraphic from "./PillarGraphic";
import { phdResearchProjects } from "../data/phdResearchProjects";
import { researchPillars, researchAgendaIntro } from "../data/researchPillars";
import { publications } from "../data/publications";
import { filterShownProjects } from "../utils/projects";
import { getPillarVenues } from "../utils/pillarContent";
import { getProjectVenues } from "../utils/projectContent";
import EditableRegion from "../studio/EditableRegion";
import { useStudio } from "../studio/StudioContext";

const PROJECT_GRID_CLASSES =
  "grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-5 lg:gap-8";

const VIEW_AGENDA = "agenda";
const VIEW_PROJECTS = "projects";

const PhdProjectGrid = ({ projects, onOpenProject, studioMode, pubsSource }) => (
  <div className={PROJECT_GRID_CLASSES}>
    {projects.map((item) => (
      <EditableRegion
        key={item.id}
        regionId={`phdProject:${item.id}`}
        label={item.title || item.id}
        className="h-full"
      >
        <ProjectCard
          id={item.id}
          img={item.img}
          title={item.title}
          year={item.duration}
          description={item.summary}
          pillarIds={item.pillarIds}
          tags={getProjectVenues(item, pubsSource)}
          onClick={studioMode ? undefined : () => onOpenProject(item.id)}
          ctaLabel="Explore project →"
        />
      </EditableRegion>
    ))}
  </div>
);

const ViewToggle = ({ view, onChange }) => (
  <div
    className="inline-flex rounded-lg border border-gray-200 p-0.5 text-sm dark:border-gray-700"
    role="tablist"
    aria-label="Browse research by"
  >
    {[
      { id: VIEW_AGENDA, label: "By Question" },
      { id: VIEW_PROJECTS, label: "By Project" },
    ].map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={view === option.id}
        onClick={() => onChange(option.id)}
        className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
          view === option.id
            ? "bg-green-solid text-white"
            : "text-gray-500 hover:text-green dark:text-gray-400 dark:hover:text-green-dark"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/**
 * Full research browser for Home: agenda / projects toggle + pillar modal.
 * Preserves Visual Studio regions research.agendaIntro and pillar:{id}.
 */
const ResearchSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { studioMode, draft } = useStudio();
  const pillars =
    studioMode && draft?.researchPillars
      ? draft.researchPillars
      : researchPillars;
  const agendaIntro =
    studioMode && draft?.researchAgendaIntro
      ? draft.researchAgendaIntro
      : researchAgendaIntro;
  const pubsSource =
    studioMode && draft?.publications ? draft.publications : publications;
  const projectsSource =
    studioMode && draft?.phdResearchProjects
      ? draft.phdResearchProjects
      : phdResearchProjects;

  const viewParam = searchParams.get("view");
  const view =
    viewParam === VIEW_PROJECTS || viewParam === VIEW_AGENDA
      ? viewParam
      : VIEW_AGENDA;
  const activePillarId = searchParams.get("pillar") || "";
  const activeProjectId = searchParams.get("project") || "";

  const updateParams = (mutator) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next, { replace: true });
  };

  const setView = (nextView) => {
    updateParams((next) => {
      next.set("view", nextView);
      next.delete("project");
      if (nextView === VIEW_AGENDA) {
        // Keep pillar for opening the briefing modal
      } else if (!next.get("pillar")) {
        next.delete("pillar");
      }
    });
  };

  const openPillar = (pillarId) => {
    updateParams((next) => {
      next.delete("project");
      next.set("view", VIEW_AGENDA);
      next.set("pillar", pillarId);
    });
  };

  const closePillar = () => {
    updateParams((next) => {
      next.delete("pillar");
      next.set("view", VIEW_AGENDA);
    });
  };

  const openProject = (projectId) => {
    updateParams((next) => {
      next.delete("pillar");
      next.set("view", VIEW_PROJECTS);
      next.set("project", projectId);
    });
  };

  const closeProject = () => {
    updateParams((next) => {
      next.delete("project");
    });
  };

  const shownPhd = filterShownProjects(projectsSource);
  const filteredPhd =
    view === VIEW_PROJECTS && activePillarId
      ? shownPhd.filter((project) =>
          (project.pillarIds || []).includes(activePillarId),
        )
      : shownPhd;

  const modalPillarId =
    view === VIEW_AGENDA && !activeProjectId ? activePillarId : "";
  const modalProjectId = activeProjectId;

  return (
    <section
      id="research-agenda"
      className="scroll-mt-20 text-left border-gray-200 pt-0 dark:border-gray-700 lg:border-t lg:pt-5"
    >
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText sm:mb-1.5">
          Research Themes
        </h2>
        <ViewToggle view={view} onChange={setView} />
      </div>

      <EditableRegion regionId="research.agendaIntro" label="Agenda intro">
        <p className="mt-3 mb-1.5 text-base font-semibold leading-snug text-gray-700 dark:text-gray-300 sm:mt-0">
          {agendaIntro.title}
        </p>
      </EditableRegion>
      <p className="mb-8 sm:mb-4 text-sm text-gray-500 dark:text-gray-400">
        {view === VIEW_AGENDA
          ? agendaIntro.focus
          : "I focus on how people engage with AI in conversation and decision-making in domains where emotional vulnerability is present, values and preferences are uncertain, and decisions affect family, caregivers, and support relationships, reshaping trust, responsibility, and authority between people."}
      </p>

      {view === VIEW_AGENDA ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
          {pillars.map((pillar) => (
            <EditableRegion
              key={pillar.id}
              regionId={`pillar:${pillar.id}`}
              label={pillar.bigGoal || pillar.id}
              className="h-full"
            >
              <ProjectCard
                variant="pillar"
                media={
                  <PillarGraphic
                    pillarId={pillar.id}
                    className="h-full w-full max-h-full"
                  />
                }
                title={pillar.bigGoal || pillar.shortLabel || pillar.id}
                description={pillar.title}
                tags={getPillarVenues(pillar.id, pubsSource)}
                onClick={
                  studioMode ? undefined : () => openPillar(pillar.id)
                }
                ctaLabel="Explore this question →"
              />
            </EditableRegion>
          ))}
        </div>
      ) : (
        <div id="phd-research">
          {filteredPhd.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No PhD projects match this agenda filter.
            </p>
          ) : (
            <PhdProjectGrid
              projects={filteredPhd}
              onOpenProject={openProject}
              studioMode={studioMode}
              pubsSource={pubsSource}
            />
          )}
        </div>
      )}

      <PillarModal
        pillarId={modalPillarId || null}
        onClose={closePillar}
      />
      <ProjectModal
        projectId={modalProjectId || null}
        onClose={closeProject}
        onOpenPillar={openPillar}
      />
    </section>
  );
};

export default ResearchSection;
